import {
  AutoStack,
  DayPickerStrings,
  FormikGraphQLDatePicker,
  FormikProvingGroundDropdown,
  FormikResourceCategoryDropdown,
  FormikResourceDropdown,
  FormikResourceTypeDropdown,
  onFormatDate,
} from 'components'
import {
  Field,
  FieldArray,
  FieldProps,
  FormikConsumer,
  FormikContext,
} from 'formik'
import { FormikCheckbox, FormikTextField } from 'formik-office-ui-fabric-react'
import { Box } from 'grid'
import { ResourceInsertType, ResourceSetType, UsageTimeSpan } from 'model'
import {
  DayOfWeek,
  DefaultButton,
  IconButton,
  Label,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FieldStack, FieldStackItem, FormStack } from '../FormStack'

export type ResourceFormValues = ResourceInsertType | ResourceSetType

Yup.setLocale({
  mixed: {
    required: 'Dieses Feld ist Erforderlich',
  },
})
export class ResourceFormSchema {
  constructor(category?: string, type?: string) {
    const validationObj: { [key: string]: any } = {
      title: Yup.string().required().trim(),
      type: Yup.string().required().trim(),
      category: Yup.string().required().trim(),
      external: Yup.bool(),
      usageTimespans: Yup.array().when('external', {
        is: true,
        then: Yup.array()
          .of(
            Yup.object({
              dateFrom: Yup.date().required(),
              dateTo: Yup.date().required(),
            })
          )
          .min(1),
      }),
      // rootId: Yup.string()
      //   .required()
      //   .trim(),
    }

    switch (category) {
      case 'workshop':
        validationObj.parentId = Yup.string().required()
        break
      case 'office':
        validationObj.parentId = Yup.string().required()
        break
      case 'fleet':
        validationObj.chassisNumber = Yup.string().required()
        break
      default:
        break
    }

    switch (type) {
      case 'testTrackUsage':
        validationObj.parentId = Yup.string().required()
        break
      default:
        break
    }

    return Yup.object().shape(validationObj)
  }
}

// tslint:disable-next-line:variable-name
const ResourceFormWithContext: React.FunctionComponent<
  FormikContext<ResourceFormValues>
> = (ctx) => {
  const { t } = useTranslation()
  const [isExternal, setExternal] = React.useState(false)
  const { _id, category, type, rootId } = ctx.values || ({} as any)
  const parentArgs = React.useMemo<null | {
    category: string
    type?: string
    excludeTypes?: string[]
    onlyIncludeType?: string
  }>(() => {
    switch (type) {
      case 'workstation':
        return {
          category: 'office',
          excludeTypes: ['workstation'],
        }
      case 'conferenceRoom':
      case 'officeRoom':
      case 'officeContainer':
        return { category: 'building' }
      case 'testTrackUsage':
        return { category: 'testing', excludeTypes: ['testTrackUsage'] }
      case 'liftingPlatform':
        return { category: 'workshop', excludeTypes: ['liftingPlatform'] }
      case 'garage':
        return { category: 'building' }
      case 'accessCard':
      case 'interimBadge':
      case 'radio':
      case 'remoteControl':
        return {
          category: 'accessory',
          excludeTypes: [
            'accessCard',
            'interimBadge',
            'radio',
            'remoteControl',
          ],
          // onlyIncludeType: 'set',
        } // TODO integrate type: set
      case 'room':
        return {
          category: 'lodging',
          excludeTypes: ['room'],
        }
      default:
        return null
    }
  }, [type])

  const resetUsageTimespans = React.useCallback(
    (fieldProps: FieldProps<any>) => {
      const timeSpans = fieldProps.form.values.usageTimespans
      if (timeSpans && timeSpans.length > 0) {
        fieldProps.form.setFieldValue('usageTimespans', [])
      }
    },
    []
  )

  return (
    <FormStack>
      <FieldStack>
        <FieldStackItem
          name="rootId"
          label={t('field.provingGround.label')}
          required={true}
          disabled={_id}
          component={FormikProvingGroundDropdown}
        />
        <Label required={true}>{t('field.resource.label')}</Label>
        <FieldStack horizontal={true}>
          <FieldStackItem
            name="category"
            disabled={_id}
            render={(fieldProps) => (
              <FormikResourceCategoryDropdown disabled={_id} {...fieldProps} />
            )}
          />
          <FieldStackItem
            name="type"
            render={(fieldProps) => (
              <FormikResourceTypeDropdown category={category} {...fieldProps} />
            )}
          />
        </FieldStack>
        {parentArgs && (
          <FieldStackItem
            name="parentId"
            render={(fieldProps) => (
              <FormikResourceDropdown
                label={t('field.parent.label')}
                required={true}
                rootId={rootId}
                {...parentArgs}
                {...fieldProps}
              />
            )}
          />
        )}
        {category === 'coldCell' && (
          <FieldStack>
            <FieldStackItem
              name="location"
              label={t('field.location.label')}
              component={FormikTextField}
            />
          </FieldStack>
        )}
        <FieldStackItem
          name="title"
          label={t('field.title.label')}
          required={true}
          component={FormikTextField}
        />
        {(category === 'building' ||
          category === 'office' ||
          type === 'testTrack' ||
          type === 'testTrackUsage' ||
          category === 'ferryConnection' ||
          type === 'fuel' ||
          type === 'chargingInfrastructure' ||
          category === 'coldCell' ||
          category === 'accessory' ||
          category === 'workshop') && (
          <FieldStack horizontal={true}>
            <FieldStackItem
              name="unit"
              label={t('field.unit.label')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="costPerUnit"
              label={t('field.costPerUnit.label')}
              component={FormikTextField}
              type="number"
              suffix="€"
            />
          </FieldStack>
        )}

        {category === 'ferryConnection' && (
          <FieldStack horizontal={true}>
            <FieldStackItem
              name="origin"
              label={t('field.origin.label')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="destination"
              label={t('field.destination.label')}
              component={FormikTextField}
            />
          </FieldStack>
        )}
        {category === 'lodging' && (
          <FieldStack>
            <FieldStackItem
              name="address"
              label={t('Adresse')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="place"
              label={t('Ort')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="land"
              label={t('Land')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="phone"
              label={t('Telefon')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="fax"
              label={t('Fax')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="contact"
              label={t('Direkter Kontakt')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="email"
              label={t('E-Mail')}
              component={FormikTextField}
            />
            <FieldStackItem
              name="emailHRG"
              label={t('E-Mail Reisebüro')}
              component={FormikTextField}
            />
          </FieldStack>
        )}
        {(type === 'testTrack' || type === 'testTrackUsage') && (
          <FieldStackItem
            name="notification"
            label={t('field.notification.label')}
            multiline={true}
            rows={3}
            component={FormikTextField}
          />
        )}
        <FieldStackItem
          name="annotation"
          label={t('field.annotation.label')}
          multiline={true}
          rows={3}
          component={FormikTextField}
        />
      </FieldStack>
      <FieldStackItem
        stack={{ grow: 0 }}
        name="external"
        render={(fieldProps) => {
          setExternal(fieldProps.field.value)
          if (
            !fieldProps.field.value &&
            fieldProps.form.values.usageTimespans !== undefined
          ) {
            resetUsageTimespans(fieldProps)
          }
          return (
            <>
              <Label>{t('field.external.label')}</Label>
              <Box py={1}>
                <FormikCheckbox {...fieldProps} />
              </Box>
            </>
          )
        }}
      />
      {isExternal && (
        <>
          <FieldArray name="usageTimespans">
            {({ form, name, ...helpers }) => {
              const values: UsageTimeSpan[] = form.values[name] || []

              return (
                <AutoStack tokens={{ childrenGap: 4 }}>
                  {values.map((x, i: any) => (
                    <AutoStack
                      horizontal={true}
                      verticalAlign="center"
                      tokens={{ childrenGap: 8 }}
                      key={i}
                    >
                      <Field
                        name={`usageTimespans[${i}].dateFrom`}
                        component={FormikGraphQLDatePicker}
                        firstDayOfWeek={DayOfWeek.Monday}
                        strings={DayPickerStrings}
                        showWeekNumbers={true}
                        formatDate={onFormatDate}
                      />
                      <Field
                        name={`usageTimespans[${i}].dateTo`}
                        component={FormikGraphQLDatePicker}
                        initialPickerDate={new Date(x.dateFrom)}
                        firstDayOfWeek={DayOfWeek.Monday}
                        minDate={new Date(x.dateFrom)}
                        disables={!x.dateFrom}
                        strings={DayPickerStrings}
                        showWeekNumbers={true}
                        formatDate={onFormatDate}
                      />
                      {
                        <IconButton
                          styles={{ root: { color: 'red' } }}
                          iconProps={{ iconName: 'Delete' }}
                          onClick={() => helpers.remove(i)}
                        />
                      }
                    </AutoStack>
                  ))}
                  {values.length < 10 && (
                    <DefaultButton
                      iconProps={{ iconName: 'Add' }}
                      onClick={() => helpers.push({})}
                      text={`Zeitraum hinzufügen (noch ${10 - values.length})`}
                    />
                  )}
                </AutoStack>
              )
            }}
          </FieldArray>
        </>
      )}
      {category === 'fleet' && (
        <FieldStack>
          <FieldStackItem
            name="color"
            label={t('field.color.label')}
            component={FormikTextField}
          />
          <FieldStackItem
            name="licensePlate"
            label={t('field.licensePlate.label')}
            component={FormikTextField}
          />
          <FieldStackItem
            name="chassisNumber"
            required={true}
            label={t('field.chassisNumber.label')}
            component={FormikTextField}
          />
          <FieldStack horizontal={true}>
            <FieldStackItem
              stack={{ grow: 0 }}
              name="tuevSweden"
              render={(fieldProps) => (
                <>
                  <Label>{t('field.tuevSweden.label')}</Label>
                  <Box py={1}>
                    <FormikCheckbox {...fieldProps} />
                  </Box>
                </>
              )}
            />
            <FieldStackItem
              name="tuevUntil"
              label={t('field.tuevUntil.label')}
              component={FormikTextField}
            />
          </FieldStack>
        </FieldStack>
      )}

      {_id && (
        <FieldStack>
          <Box py={4}>
            <FieldStackItem
              stack={{ grow: 0 }}
              name="inactive"
              render={(fieldProps) => (
                <>
                  <Label>{t('field.inactive.label')}</Label>
                  <Box py={1}>
                    <FormikCheckbox {...fieldProps} />
                  </Box>
                </>
              )}
            />
          </Box>
        </FieldStack>
      )}
    </FormStack>
  )
}

export const ResourceForm: React.FunctionComponent = () => (
  <FormikConsumer>
    {(ctx) => <ResourceFormWithContext {...ctx} />}
  </FormikConsumer>
)
